import { useState, useEffect, useRef, Component, type ReactNode, type ErrorInfo } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Sidebar } from '@/components/layout/Sidebar'
import { TopBar } from '@/components/layout/TopBar'
import { MainContent } from '@/components/layout/MainContent'
import { ParallaxBackground } from '@/components/layout/ParallaxBackground'
import { TimerWidget } from '@/components/layout/TimerWidget'
import { ContextPanel } from '@/components/layout/ContextPanel'
import { ApprovalCentreProvider } from '@/components/approvals/ApprovalCentreProvider'
import { OpeningIntro } from '@/components/layout/OpeningIntro'
import { EnvironmentModeBanner } from '@/components/layout/EnvironmentModeBanner'

import { useTheme } from '@/hooks/useTheme'
import { useEventStream } from '@/hooks/useEventStream'
import { isAuthenticated, saveToken } from '@/lib/auth'
import { syncAuthTokenToServiceWorker } from '@/lib/push'
import { useReminderChecker } from '@/hooks/useWidgetUtils'
import { useAppStore } from '@/store'

const ADMIN_GATEWAY_URL = 'https://ops-admin-gateway.ops-a58.workers.dev'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
})

function AppShell() {
  useTheme()
  useEffect(() => {
    const root = document.documentElement
    try {
      const textScale = localStorage.getItem('opsbridge-text-scale')
      const headingScale = localStorage.getItem('opsbridge-heading-scale')
      const density = localStorage.getItem('opsbridge-layout-density')
      if (textScale && Number.isFinite(Number(textScale))) root.style.setProperty('--text-scale', textScale)
      if (headingScale && Number.isFinite(Number(headingScale))) root.style.setProperty('--heading-scale', headingScale)
      if (density === 'compact' || density === 'comfortable' || density === 'spacious') root.dataset.density = density
    } catch { /* device preference storage unavailable */ }
  }, [])
  useEventStream()
  // Runs globally, independent of whether the Calendar panel is currently
  // open -- a reminder set once should keep firing on schedule even after
  // the panel that created it is closed, not only while it's visible.
  useReminderChecker()
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const setActiveSessionId = useAppStore((s) => s.setActiveSessionId)
  const setActiveSection = useAppStore((s) => s.setActiveSection)
  const activeSection = useAppStore((s) => s.activeSection)
  const environmentMode = useAppStore((s) => s.environmentMode)

  // --- Mobile drawer drag gesture ---
  // The previous drag build lived inside Sidebar.tsx and correctly worked,
  // but only controlled sidebarCollapsed (icon-rail vs full-width *within*
  // the panel) -- on mobile the panel's actual on/off-screen visibility is
  // this separate mobileSidebarOpen state, translated via CSS classes
  // below. That's the real reason swipe-to-close did nothing: it was
  // never wired to this state at all. Fixed here, at the layer that
  // actually owns on/off-screen visibility.
  //
  // Updated: real velocity tracking (two-sample, at release) instead of
  // distance-only, so a fast flick completes the gesture even short of
  // the distance threshold -- and the release animation's duration now
  // scales with how far the panel still has to travel from wherever the
  // finger let go, rather than a fixed 300ms regardless of start point.
  // That mismatch between felt input speed and a fixed-timing output was
  // the likely cause of it reading as rigid rather than fluid.
  const drawerSurfaceRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<number | null>(null)
  const dragFrame = useRef(0)
  const releaseTimer = useRef(0)
  const gestureEnabled = useRef(false)
  const suppressClickUntil = useRef(0)
  const sidebarWrapRef = useRef<HTMLDivElement>(null)
  const [dragTx, setDragTx] = useState<number | null>(null)
  const [releaseStyle, setReleaseStyle] = useState<{ transform: string; transition: string } | null>(null)
  const touchStart = useRef({ x: 0, y: 0 })
  const gestureDir = useRef<'horizontal' | 'vertical' | null>(null)
  const measuredWidth = useRef(280)
  const prevSample = useRef({ x: 0, t: 0 })
  const lastSample = useRef({ x: 0, t: 0 })

  const FLICK_VELOCITY = 0.5 // px/ms -- a genuine quick flick, not just a fast-ish drag
  const DISTANCE_THRESHOLD = 0.35

  const handleDrawerTouchStart = (e: TouchEvent) => {
    gestureEnabled.current = false
    if (e.touches.length !== 1) return
    const target = e.target instanceof Element ? e.target : null
    if (target?.closest('input, textarea, select, [contenteditable], [role="slider"], canvas, video, [data-no-drawer-swipe]')) return
    for (let el = target; el && el !== drawerSurfaceRef.current; el = el.parentElement) {
      if (el.scrollWidth > el.clientWidth + 4 && /auto|scroll/.test(getComputedStyle(el).overflowX)) return
    }
    gestureEnabled.current = true
    window.clearTimeout(releaseTimer.current)
    dragRef.current = null
    if (sidebarWrapRef.current) measuredWidth.current = sidebarWrapRef.current.offsetWidth || 280
    const now = performance.now()
    const x = e.touches[0].clientX
    touchStart.current = { x, y: e.touches[0].clientY }
    prevSample.current = { x, t: now }
    lastSample.current = { x, t: now }
    gestureDir.current = null
    setReleaseStyle(null)
  }

  const handleDrawerTouchMove = (e: TouchEvent) => {
    if (!gestureEnabled.current) return
    if (e.touches.length !== 1) { handleDrawerTouchCancel(); return }
    const clientX = e.touches[0].clientX
    const dx = clientX - touchStart.current.x
    const dy = e.touches[0].clientY - touchStart.current.y
    if (gestureDir.current === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
      gestureDir.current = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'horizontal' : 'vertical'
    }
    if (gestureDir.current !== 'horizontal') return
    // Opening (from the edge zone or backdrop): only rightward drag is meaningful.
    // Closing (from the open panel or backdrop): only leftward drag is meaningful.
    if ((!mobileSidebarOpen && dx < 0) || (mobileSidebarOpen && dx > 0)) return
    if (e.cancelable) e.preventDefault()
    prevSample.current = lastSample.current
    lastSample.current = { x: clientX, t: performance.now() }
    dragRef.current = dx
    if (!dragFrame.current) dragFrame.current = requestAnimationFrame(() => {
      dragFrame.current = 0
      setDragTx(dragRef.current)
    })
  }

  const handleDrawerTouchEnd = () => {
    cancelAnimationFrame(dragFrame.current)
    dragFrame.current = 0
    const dragTx = dragRef.current
    gestureEnabled.current = false
    if (gestureDir.current !== 'horizontal' || dragTx === null) {
      setDragTx(null)
      gestureDir.current = null
      return
    }

    const dt = Math.max(1, lastSample.current.t - prevSample.current.t)
    const velocity = performance.now() - lastSample.current.t < 100
      ? (lastSample.current.x - prevSample.current.x) / dt : 0
    suppressClickUntil.current = performance.now() + 400
    const progress = Math.abs(dragTx) / measuredWidth.current

    let finalOpen = mobileSidebarOpen
    if (!mobileSidebarOpen && (velocity > FLICK_VELOCITY || progress > DISTANCE_THRESHOLD)) finalOpen = true
    if (mobileSidebarOpen && (velocity < -FLICK_VELOCITY || progress > DISTANCE_THRESHOLD)) finalOpen = false

    const w = measuredWidth.current
    const currentPx = Math.max(-w, Math.min(0, (mobileSidebarOpen ? 0 : -w) + dragTx))
    const targetPx = finalOpen ? 0 : -w
    const remaining = Math.abs(targetPx - currentPx)
    const duration = Math.round(Math.min(420, Math.max(220, remaining * 0.9)))

    setReleaseStyle({
      transform: `translateX(${targetPx}px)`,
      transition: `transform ${duration}ms cubic-bezier(0.22, 0.61, 0.36, 1)`,
    })
    if (finalOpen !== mobileSidebarOpen) setMobileSidebarOpen(finalOpen)

    setDragTx(null)
    gestureDir.current = null
    dragRef.current = null
    releaseTimer.current = window.setTimeout(() => setReleaseStyle(null), duration + 30)
  }

  const handleDrawerTouchCancel = () => {
    gestureEnabled.current = false
    gestureDir.current = null
    dragRef.current = null
    cancelAnimationFrame(dragFrame.current)
    dragFrame.current = 0
    setDragTx(null)
    setReleaseStyle(null)
  }

  const drawerLiveStyle = (() => {
    if (dragTx !== null) {
      const w = measuredWidth.current
      const base = mobileSidebarOpen ? 0 : -w
      const raw = base + dragTx
      const clamped = Math.max(-w, Math.min(0, raw))
      return { transform: `translateX(${clamped}px)`, transition: 'none' as const }
    }
    return releaseStyle ?? {
      transform: mobileSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
      transition: 'transform 340ms cubic-bezier(0.22, 0.61, 0.36, 1)',
    }
  })()

  useEffect(() => { syncAuthTokenToServiceWorker() }, [])

  // Handle deep links. Approval links only navigate to Governance/Review;
  // opening a link never approves or consumes a token.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const approvalFromUrl = params.get('approval')
    const sessionFromUrl = params.get('session') || sessionStorage.getItem('ops_pending_session')
    sessionStorage.removeItem('ops_pending_session')

    if (approvalFromUrl) setActiveSection('governance')

    if (sessionFromUrl && !approvalFromUrl) {
      setActiveSessionId(sessionFromUrl)
      setActiveSection('chat')
    }

    if (params.has('session')) {
      params.delete('session')
      const next = params.toString()
      window.history.replaceState({}, '', window.location.pathname + (next ? '?' + next : ''))
    }
  }, [setActiveSessionId, setActiveSection])

  // Use matchMedia instead of window.innerWidth — matchMedia respects the CSS viewport
  // and cannot be overridden by Android's "Desktop mode" browser setting.
  // Also force mobile when running as an installed PWA (standalone/minimal-ui).
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    || window.matchMedia('(display-mode: minimal-ui)').matches
    || (window.navigator as Navigator & { standalone?: boolean }).standalone === true

  const mobileQuery = window.matchMedia('(max-width: 767px)')
  const [isMobile, setIsMobile] = useState(isStandalone || mobileQuery.matches)

  useEffect(() => {
    // Always mobile in standalone PWA mode — ignore resize
    if (isStandalone) return
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mobileQuery.addEventListener('change', handler)
    return () => mobileQuery.removeEventListener('change', handler)
  }, [isStandalone])

  useEffect(() => {
    const surface = drawerSurfaceRef.current
    if (!isMobile || !surface) return
    const click = (event: MouseEvent) => {
      if (performance.now() < suppressClickUntil.current) {
        event.preventDefault(); event.stopPropagation()
      }
    }
    surface.addEventListener('touchstart', handleDrawerTouchStart, { passive: true })
    surface.addEventListener('touchmove', handleDrawerTouchMove, { passive: false })
    surface.addEventListener('touchend', handleDrawerTouchEnd)
    surface.addEventListener('touchcancel', handleDrawerTouchCancel)
    surface.addEventListener('click', click, true)
    return () => {
      surface.removeEventListener('touchstart', handleDrawerTouchStart)
      surface.removeEventListener('touchmove', handleDrawerTouchMove)
      surface.removeEventListener('touchend', handleDrawerTouchEnd)
      surface.removeEventListener('touchcancel', handleDrawerTouchCancel)
      surface.removeEventListener('click', click, true)
    }
  }, [isMobile, mobileSidebarOpen])

  useEffect(() => () => {
    cancelAnimationFrame(dragFrame.current)
    window.clearTimeout(releaseTimer.current)
  }, [])

  return (
    <div ref={drawerSurfaceRef} className={`flex h-full relative overflow-hidden w-full ${environmentMode === 'sandbox' ? 'pt-12' : ''}`}>
      <EnvironmentModeBanner />
      <ParallaxBackground />
      <TimerWidget />

      {/* Mobile sidebar overlay */}
      {isMobile && (mobileSidebarOpen || dragTx !== null) && (
        <div
          className="fixed inset-0 z-40 bg-black/60"
          style={{
            opacity: dragTx !== null ? Math.max(0, Math.min(1, (mobileSidebarOpen ? 1 : 0) + dragTx / measuredWidth.current)) : 1,
            transition: dragTx !== null ? 'none' : 'opacity 340ms ease-out',
            pointerEvents: mobileSidebarOpen ? 'auto' : 'none',
          }}
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        ref={sidebarWrapRef}
        style={isMobile ? drawerLiveStyle : undefined}
        className={
          isMobile
            ? 'fixed inset-y-0 left-0 z-50 will-change-transform'
            : 'relative'
        }>
        <Sidebar mobileDrawer={isMobile} onNavClick={() => isMobile && setMobileSidebarOpen(false)} />
      </div>

      {!isMobile && <ContextPanel />}

      {/* Main area */}
      <div className="flex flex-col flex-1 min-w-0">
        {!(isMobile && activeSection === 'chat') && (
          <TopBar
            onMenuClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            isMobile={isMobile}
          />
        )}
        <div className="flex flex-1 overflow-hidden">
          <MainContent isMobile={isMobile} onOpenMobileSidebar={() => setMobileSidebarOpen(true)} />
        </div>
      </div>
    </div>
  )
}

function AuthGate({ allowRedirect }: { allowRedirect: boolean }) {
  const [authed, setAuthed] = useState(isAuthenticated)
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    // Login now happens on the Admin Gateway, not in this app -- pick up
    // the token it hands back via redirect, if present, then clean the URL.
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    const session = params.get('session')
    if (session) sessionStorage.setItem('ops_pending_session', session)
    if (token) {
      saveToken(token)
      window.history.replaceState({}, '', window.location.pathname)
      setAuthed(true)
    }
    setChecked(true)
  }, [])

  if (!checked) return null

  if (!authed) {
    if (allowRedirect) window.location.href = ADMIN_GATEWAY_URL
    return null
  }

  return (
    <ApprovalCentreProvider>
      <AppShell />
    </ApprovalCentreProvider>
  )
}

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { error: null }
  }
  static getDerivedStateFromError(error: Error) { return { error } }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error('[ErrorBoundary]', error, info) }
  render() {
    if (this.state.error) {
      return (
        <div className="flex h-full items-center justify-center bg-[#020408] text-white">
          <div className="text-center max-w-lg px-6">
            <div className="text-red-400 font-mono text-xs mb-4 uppercase tracking-widest">Render Error</div>
            <div className="text-sm text-white/60 font-mono mb-6 bg-white/5 rounded-lg p-4 text-left break-all">
              {this.state.error.message}
            </div>
            {this.state.error.stack && (
              <div className="text-[10px] text-white/40 font-mono mb-6 bg-white/5 rounded-lg p-3 text-left break-all max-h-48 overflow-y-auto whitespace-pre-wrap">
                {this.state.error.stack}
              </div>
            )}
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-medium transition-colors"
            >Reload</button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

export default function App() {
  const [introComplete, setIntroComplete] = useState(false)

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthGate allowRedirect={introComplete} />
        {!introComplete && <OpeningIntro onComplete={() => setIntroComplete(true)} />}
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
